import {
    SuperBit_swaggerClient,
    FilterDataModel,
    ProjectsModel as ProjectsDataModel,
    ProjectModel,
    SelectListItem,
    BranchesDepartmentsUsersModel,
    ProjectUsersModel,
} from './superbit-api-models';
import generalSettings from '../json/general-settings.json';

// Service wrapper for the System Management "Projects" tab.
// Replaces the old MVC Views/SystemManagement/_Projects.cshtml view and the
// ProjectController/MaintenanceController endpoints it posted to
// (projectSort / projectUpsert / projectDelete / projectAdd).
export class ProjectsModel {
    client: any;

    constructor() {
        const apiUrl = localStorage.getItem('apiUrl');
        const url = apiUrl ? apiUrl : generalSettings.apiUrl;

        // Some generated client methods omit the Authorization header; inject it
        // here (only when absent) so every call through this client is authenticated.
        const authHttp = {
            fetch(input: RequestInfo, init?: RequestInit) {
                const token = localStorage.getItem('AuthToken');
                const headers: any = { ...(init && init.headers ? init.headers : {}) };
                if (token && !headers['Authorization']) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                return window.fetch(input, { ...(init || {}), headers });
            },
        };

        this.client = new SuperBit_swaggerClient(url, authHttp);
    }

    // Sorted/filtered/paged project list (replaces _ProjectItems.cshtml + _Paging.cshtml).
    // Each project carries its child branches (usersBranches) and users
    // (usersInProject); paging is server-side over the top-level projects and
    // allRowsCounter is the total number of projects.
    async projectSort(filter: FilterDataModel): Promise<ProjectsDataModel> {
        return await this.client.projectSort(filter);
    }

    // Create AND edit both post through projectUpsert (the server upserts by Id),
    // mirroring the old MVC _NewProject form which always posted to Project/Upsert.
    async projectUpsert(model: ProjectModel): Promise<void> {
        return await this.client.projectUpsert(model);
    }

    // Deletes a project (server-side cascade in ProjectBL.Delete).
    async projectDelete(id: string): Promise<void> {
        return await this.client.projectDelete(id);
    }

    // Loads the list of users used to populate the "project manager" dropdown in
    // the add/edit modal. projectAdd returns a fresh model whose `users` list is
    // the full user SelectList (the same list the old _NewProject form bound to).
    async getProjectManagers(): Promise<SelectListItem[]> {
        const model = await this.client.projectAdd(undefined);
        return model && model.users ? model.users : [];
    }

    // Loads the branch/department/user tree for the "add users" modal, replacing
    // the old MVC Project/GetAddUsersView. The returned model carries the full
    // branch tree (branches), all users (each with branchId) and activeUsers — the
    // ids of users already attached to the project (pre-checked in the tree).
    async getAddUsersView(projectId: string): Promise<BranchesDepartmentsUsersModel> {
        return await this.client.projectGetAddUsersView(projectId);
    }

    // Saves the user/branch selection for a project (replaces Project/AddUsers).
    // `branches` holds only the top-most fully-selected branch ids and `users`
    // only individually-selected users, matching the old form's minimal payload.
    async addUsers(projectId: string, userIds: string[], branchIds: string[]): Promise<void> {
        const body = new ProjectUsersModel();
        body.projectId = projectId;
        body.users = userIds;
        body.branches = branchIds;
        await this.client.projectAddUsers(body);
    }
}
